import FnAntModal from '../../components/blocks/FnAntModal/index';
import { FnAntModalWidth } from '../../components/blocks/FnAntModal/constants';
import { BASE_DATE_FORMAT } from '../../constants/date';
import { AxiosResponse } from 'axios';
import clsx from 'clsx';
import React, { FC, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import Moment from 'react-moment';
import { TBaseRequest } from '../../commonTypes/basic';
import { TCurrentFilter, TSort } from '../../commonTypes/filters';
import FnLeftHeaderBlock from '../../components/blocks/FnDataTable/components/FnLeftHeaderBlock/index';
import FnDataTable from '../../components/blocks/FnDataTable/index';
import { URLS } from '../../constants/urls';
import axiosInstance from '../../core/interceptor';
import ModalFiltersNativeWrapper from '../Filters/ModalFilters/ModalFiltersNativeWrapper';
import { INITIAL_REQUEST, TABLE_COLUMNS_BOM, TABLE_COLUMNS_KSF } from './constants';
import MockupStatusReducer, { initialState, resetData, setData, setIsLoading } from './redux/slices';
import { TMockupBOM, TMockupKSF, TMockupStatusResponse, TTMockupBOMZip } from './redux/types';
import { TDirectoryName } from '@commonTypes/order';

type TProps = {
  title: string;
  standartId: string;
  createdDate: string;
  requestId: string | null;
  accountId: string;
  apiUrl: string;
  urlPath: string;
  requestPageType: string;
  directoryName?: TDirectoryName;
  isKSF: boolean;
  isOpen: boolean;
  onClose: () => void;
};

export const MockupStatusModal: FC<TProps> = ({
  title,
  standartId,
  createdDate,
  requestId,
  accountId,
  apiUrl,
  urlPath,
  requestPageType,
  directoryName = 'KSF',
  isKSF,
  isOpen,
  onClose
}) => {
  const [state, dispatch] = useReducer(MockupStatusReducer, initialState);
  const { totalCount, data: mockupStatusData, isLoading } = state;
  const zipUri = {
    url: `${apiUrl}${URLS.SKU_DOWNLOAD_BOM_FILES}/`,
    correlationId: requestId
  };

  const [request, setRequest] = useState<TBaseRequest>({
    ...INITIAL_REQUEST,
    requestPage: requestPageType
  });
  const [filterData, setFilterData] = useState(null);
  const [isFirstSortInit, setIsFirstSortInit] = useState(true);

  // It works as onMount
  useEffect(() => {
    updateData({
      ...request,
    });
  }, [accountId]);

  const updateData = (request: TBaseRequest) => {
    setRequest(request);

    dispatch(setIsLoading(true));

    let url = `${apiUrl}${urlPath}`;

    if (requestId) {
      url = `${url}/${requestId}`;
    }

    if (isKSF) {
      url = `${url}/${directoryName}`;
    }

    axiosInstance
      .post(url, { ...request, accountId })
      .then((response: AxiosResponse<TMockupStatusResponse>) => {
        const data = response.data.data;
        const newData: TMockupKSF[] | TTMockupBOMZip[] = !isKSF
          ? (data as TMockupBOM[]).map((el) => ({ ...el, zipUri }))
          : (data as TMockupKSF[]);

        dispatch(setData({ ...response.data, data: newData }));
        setFilterData(response.data.filterData);
      })
      .catch(() => dispatch(setIsLoading(false)));
  };

  const handlePageChange = useCallback(
    (page: number) => {
      updateData({
        ...request,
        page
      });
    },
    [request]
  );

  const handleSortChange = useCallback(
    (data: TSort) => {
      if (!isFirstSortInit) {
        const newSortItems = data ? [{ sortColumn: data.sortColumn, sortDir: data.sortDir }] : [];
        updateData({
          ...request,
          page: 1,
          sortItems: newSortItems
        });
      }
      setIsFirstSortInit(false);
    },
    [request, updateData]
  );

  const handleFilterChange = useCallback(
    (data: TCurrentFilter[]) => {
      updateData({
        ...request,
        page: 1,
        filterItems: data
      });
    },
    [request, updateData]
  );

  useEffect(() => {
    return () => dispatch(resetData());
  }, []);

  const countComponent = useMemo(
    () => (
      <FnLeftHeaderBlock
        type={isKSF ? 'All Directories' : 'All product variants'}
        totalCountText={isKSF ? `${totalCount} Directories` : `${totalCount} variants`}
      />
    ),
    [isKSF, totalCount]
  );
  
  return (
    <FnAntModal
      className="fixed-table"
      titleText={`${isKSF ? `${directoryName} Directories` : 'Bill of Materials'} - ${title} ${!standartId ? '' : '(' + standartId + ')'}`}
      titleSecondLineLabel="Last Update:"
      titleSecondLineValue={createdDate ? <Moment format={BASE_DATE_FORMAT} date={createdDate} /> : ''}
      width={FnAntModalWidth.Xl}
      isOpen={true}
      onClose={onClose}
      hasFilter={true}
      zIndex={1041}
    >
      <div className="scroll-table-grid">
        <div className="filter-wrapper">
          <ModalFiltersNativeWrapper
            filterData={filterData}
            currentAccountId={accountId}
            onFilterChange={handleFilterChange}
            onSortChange={handleSortChange}
            isOpen={isOpen}
            url={apiUrl}
            requestURL="SKUCatalog/filter"
            requestPage={requestPageType}
          />
        </div>

        <FnDataTable
          className={clsx({ ['bom-table']: !isKSF })}
          leftHeaderBlock={countComponent}
          loading={isLoading}
          columns={isKSF ? TABLE_COLUMNS_KSF : TABLE_COLUMNS_BOM}
          data={mockupStatusData}
          currentPage={request.page}
          rowCount={totalCount}
          stickyHead
          rowsPerPage={request.pageLimit}
          onChangePage={handlePageChange}
        />
      </div>
    </FnAntModal>
  );
};
